/**
 * AdminMarketWatchPage (Jul-2026, User-Wunsch) — Marktbeobachtung:
 * Quellen-Verwaltung (Börsen, Medien, Social, Auktionen, Hersteller),
 * gesammelte KI-Themen und Newsletter-Postfach (IMAP).
 */
import { useEffect, useState, useCallback } from "react";
import { Radar, Plus, Trash2, RefreshCw, Loader2, Mail, Globe, Save } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const CAT_LABEL = { boerse: "Börse", medien: "Medien", social: "Social Media", auktion: "Auktion", hersteller: "Hersteller", sonstiges: "Sonstiges" };
const CAT_COLOR = { boerse: "bg-amber-100 text-amber-800", medien: "bg-sky-100 text-sky-800", social: "bg-violet-100 text-violet-800", auktion: "bg-rose-100 text-rose-800", hersteller: "bg-emerald-100 text-emerald-800", sonstiges: "bg-gray-100 text-gray-700" };

function SourceForm({ onAdded }) {
    const [url, setUrl] = useState(""); const [label, setLabel] = useState(""); const [cat, setCat] = useState("medien"); const [busy, setBusy] = useState(false);
    const add = async () => {
        if (!url.trim()) return;
        setBusy(true);
        try {
            await api.post("/admin/market-watch/sources", { url: url.trim(), label: label.trim(), category: cat });
            toast.success("Quelle hinzugefügt"); setUrl(""); setLabel(""); onAdded();
        } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
        finally { setBusy(false); }
    };
    return (
        <div className="flex flex-wrap items-center gap-2 bg-white border border-gray-200 rounded-sm p-3" data-testid="mw-source-form">
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" className="flex-1 min-w-[220px] rounded-sm border border-gray-300 px-3 py-2 text-sm" data-testid="mw-source-url" />
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Bezeichnung (optional)" className="w-48 rounded-sm border border-gray-300 px-3 py-2 text-sm" data-testid="mw-source-label" />
            <select value={cat} onChange={(e) => setCat(e.target.value)} className="rounded-sm border border-gray-300 px-2 py-2 text-sm" data-testid="mw-source-cat">
                {Object.entries(CAT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <button onClick={add} disabled={busy} className="inline-flex items-center gap-1 bg-[#111827] text-white rounded-sm px-3 py-2 text-sm font-bold hover:bg-black disabled:opacity-50" data-testid="mw-source-add">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Hinzufügen
            </button>
        </div>
    );
}

// Jul-2026 (User-Wunsch): Schlagwörter, die die KI bei der Web-Recherche priorisiert.
function KeywordsPanel() {
    const [words, setWords] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => { api.get("/admin/market-watch/keywords").then((r) => setWords(r.data.keywords || [])).catch(() => {}); }, []);
    const save = async (next) => {
        setBusy(true);
        try {
            const { data } = await api.put("/admin/market-watch/keywords", { keywords: next });
            setWords(data.keywords || []);
            toast.success("Schlagwörter gespeichert — die KI priorisiert diese ab dem nächsten Crawl.");
        } catch { toast.error("Speichern fehlgeschlagen"); }
        setBusy(false);
    };
    const add = () => {
        const w = input.trim();
        if (!w) return;
        if (words.some((x) => x.toLowerCase() === w.toLowerCase())) { setInput(""); return; }
        setInput("");
        save([...words, w]);
    };
    return (
        <div className="bg-white border border-gray-200 rounded-sm p-4 mb-6" data-testid="mw-keywords-panel">
            <h2 className="text-sm font-black mb-1">Such-Schlagwörter (KI-Priorisierung)</h2>
            <p className="text-xs text-gray-500 mb-2">Marken, Modelle oder Themen, nach denen die KI gezielt recherchieren soll (z. B. „Vespa GTS", „E-Roller Förderung").</p>
            <div className="flex gap-2 mb-2">
                <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
                    placeholder="Schlagwort eingeben + Enter…" className="flex-1 rounded-sm border border-gray-300 px-3 py-2 text-sm" data-testid="mw-keyword-input" />
                <button onClick={add} disabled={busy} className="inline-flex items-center gap-1 bg-[#111827] text-white rounded-sm px-3 py-2 text-sm font-bold hover:bg-black disabled:opacity-50" data-testid="mw-keyword-add"><Plus className="w-4 h-4" /></button>
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="mw-keywords-list">
                {words.map((w) => (
                    <span key={w} className="inline-flex items-center gap-1 text-xs font-bold bg-[#FFCC00] text-[#111827] rounded-sm px-2 py-1" data-testid="mw-keyword-chip">
                        {w}
                        <button onClick={() => save(words.filter((x) => x !== w))} className="hover:text-red-700" data-testid={`mw-keyword-del-${w}`}>×</button>
                    </span>
                ))}
                {words.length === 0 && <span className="text-xs text-gray-400">Noch keine Schlagwörter.</span>}
            </div>
        </div>
    );
}

// Jul-2026 (Phase 2): Anonyme KM-/Wartungs-Statistik je Modell.
function VehicleStatsPanel() {
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get(`/admin/market-watch/vehicle-stats?q=${encodeURIComponent(q)}&limit=30`);
                setItems(data.items || []);
            } catch { setItems([]); }
            setLoading(false);
        }, 350);
        return () => clearTimeout(t);
    }, [q]);
    const fmt = (n) => (n || 0).toLocaleString("de-DE");
    return (
        <div className="bg-white border border-gray-200 rounded-sm p-4 mb-6" data-testid="mw-vehicle-stats-panel">
            <h2 className="text-sm font-black mb-1">KM- & Wartungs-Statistik je Modell (anonymisiert)</h2>
            <p className="text-xs text-gray-500 mb-2">Aggregierte Bestandsdaten (min. 2 Fahrzeuge je Gruppe): Ø KM-Stand, KM/Jahr, Ø Preis + Werkstattkosten.</p>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Marke oder Modell suchen…"
                className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm mb-2" data-testid="mw-stats-search" />
            {loading ? <div className="text-xs text-gray-400 py-2">Lädt…</div> : (
                <div className="max-h-[320px] overflow-y-auto">
                    <table className="w-full text-xs" data-testid="mw-stats-table">
                        <thead>
                            <tr className="text-left text-[9px] uppercase tracking-wider text-gray-400 border-b border-gray-200">
                                <th className="py-1">Modell</th><th className="text-right">Anz.</th><th className="text-right">Ø km</th><th className="text-right">km/Jahr</th><th className="text-right">Ø Preis</th><th className="text-right">Wartung Ø</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((r, i) => (
                                <tr key={i} className="border-b border-gray-100" data-testid={`mw-stats-row-${i}`}>
                                    <td className="py-1 font-bold truncate max-w-[180px]">{r.brand} {r.model}</td>
                                    <td className="text-right">{r.count}</td>
                                    <td className="text-right">{fmt(r.avg_km)}</td>
                                    <td className="text-right">{fmt(r.km_per_year)}</td>
                                    <td className="text-right">{fmt(Math.round(r.avg_price))} €</td>
                                    <td className="text-right">{r.avg_maintenance_cost ? `${fmt(Math.round(r.avg_maintenance_cost))} € (${r.maintenance_orders})` : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {items.length === 0 && <div className="text-xs text-gray-400 py-3 text-center">Keine Gruppen mit ≥ 2 Fahrzeugen gefunden.</div>}
                </div>
            )}
        </div>
    );
}

// Jul-2026 (Phase 2): Umfrage-Modul — Admin erstellt, Händler stimmen ab.
function SurveysAdminPanel() {
    const [items, setItems] = useState([]);
    const [question, setQuestion] = useState("");
    const [options, setOptions] = useState("");
    const load = useCallback(async () => {
        try { const { data } = await api.get("/admin/market-watch/surveys"); setItems(data.items || []); } catch { }
    }, []);
    useEffect(() => { load(); }, [load]);
    const create = async () => {
        const opts = options.split(";").map((o) => o.trim()).filter(Boolean);
        if (!question.trim() || opts.length < 2) { toast.error("Frage + mind. 2 Optionen (mit ; getrennt) angeben."); return; }
        try {
            await api.post("/admin/market-watch/surveys", { question, options: opts });
            setQuestion(""); setOptions("");
            toast.success("Umfrage erstellt — Händler sehen sie im Marktbeobachtungs-Ticker.");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    };
    const toggle = async (s) => { try { await api.patch(`/admin/market-watch/surveys/${s.id}`); load(); } catch { } };
    const del = async (s) => { try { await api.delete(`/admin/market-watch/surveys/${s.id}`); load(); } catch { } };
    return (
        <div className="bg-white border border-gray-200 rounded-sm p-4 mb-6" data-testid="mw-surveys-panel">
            <h2 className="text-sm font-black mb-1">Umfragen (Händler-Voting)</h2>
            <p className="text-xs text-gray-500 mb-2">Aktive Umfragen erscheinen bei allen Händlern im Marktbeobachtungs-Ticker (Social Studio).</p>
            <div className="grid sm:grid-cols-2 gap-2 mb-2">
                <input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Frage (z. B. Welche Marke läuft bei euch am besten?)"
                    className="rounded-sm border border-gray-300 px-3 py-2 text-sm" data-testid="mw-survey-question" />
                <div className="flex gap-2">
                    <input value={options} onChange={(e) => setOptions(e.target.value)} placeholder="Optionen mit ; trennen (z. B. Vespa;Honda;Yamaha)"
                        className="flex-1 rounded-sm border border-gray-300 px-3 py-2 text-sm" data-testid="mw-survey-options" />
                    <button onClick={create} className="bg-[#111827] text-white rounded-sm px-3 py-2 text-sm font-bold hover:bg-black" data-testid="mw-survey-create"><Plus className="w-4 h-4" /></button>
                </div>
            </div>
            <div className="space-y-2" data-testid="mw-surveys-list">
                {items.map((s) => (
                    <div key={s.id} className="border border-gray-200 rounded-sm p-2.5" data-testid={`mw-survey-${s.id}`}>
                        <div className="flex items-center gap-2">
                            <div className="flex-1 text-sm font-bold">{s.question}</div>
                            <button onClick={() => toggle(s)} className={`text-[9px] font-bold uppercase px-2 py-1 rounded-sm ${s.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`} data-testid={`mw-survey-toggle-${s.id}`}>{s.active ? "aktiv" : "beendet"}</button>
                            <button onClick={() => del(s)} className="text-red-400 hover:text-red-600" data-testid={`mw-survey-del-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                        <div className="mt-1.5 space-y-1">
                            {s.options.map((o, idx) => {
                                const pct = s.total_votes ? Math.round((s.votes[idx] / s.total_votes) * 100) : 0;
                                return (
                                    <div key={idx} className="flex items-center gap-2 text-xs">
                                        <span className="w-36 truncate">{o}</span>
                                        <div className="flex-1 h-2 bg-gray-100 rounded-sm overflow-hidden"><div className="h-full bg-[#FFCC00]" style={{ width: `${pct}%` }} /></div>
                                        <span className="w-14 text-right text-gray-500">{s.votes[idx]} ({pct}%)</span>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="text-[10px] text-gray-400 mt-1">{s.total_votes} Stimme{s.total_votes !== 1 ? "n" : ""}</div>
                    </div>
                ))}
                {items.length === 0 && <div className="text-xs text-gray-400 py-2">Noch keine Umfragen.</div>}
            </div>
        </div>
    );
}

function ImapPanel() {
    const [cfg, setCfg] = useState({ host: "", port: 993, user: "", password: "", folder: "INBOX", active: false });
    const [busy, setBusy] = useState(false);
    useEffect(() => { api.get("/admin/market-watch/imap").then((r) => setCfg((p) => ({ ...p, ...r.data.imap }))).catch(() => {}); }, []);
    const save = async () => {
        setBusy(true);
        try { await api.patch("/admin/market-watch/imap", cfg); toast.success("IMAP-Einstellungen gespeichert"); }
        catch { toast.error("Speichern fehlgeschlagen"); }
        finally { setBusy(false); }
    };
    const check = async () => {
        try {
            const r = await api.post("/admin/market-watch/check-mailbox");
            if (r.data.ok) toast.success(`Postfach geprüft: ${r.data.mails} Mails, ${r.data.topics_added} neue Themen`);
            else toast.warning(r.data.reason || "Postfach nicht erreichbar");
        } catch { toast.error("Postfach-Prüfung fehlgeschlagen"); }
    };
    return (
        <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="mw-imap-panel">
            <h3 className="text-sm font-black flex items-center gap-2 mb-1"><Mail className="w-4 h-4 text-[#FFCC00]" /> Newsletter-Postfach (IMAP)</h3>
            <p className="text-xs text-gray-500 mb-3">z. B. newsletter@twowheels.info — eingehende Newsletter & PDF-Anhänge werden per KI analysiert und liefern Themen für den Ticker.</p>
            <div className="grid sm:grid-cols-2 gap-2">
                <input value={cfg.host || ""} onChange={(e) => setCfg({ ...cfg, host: e.target.value })} placeholder="IMAP-Host (imap.example.com)" className="rounded-sm border border-gray-300 px-3 py-2 text-sm" data-testid="mw-imap-host" />
                <input value={cfg.user || ""} onChange={(e) => setCfg({ ...cfg, user: e.target.value })} placeholder="Benutzer / E-Mail" className="rounded-sm border border-gray-300 px-3 py-2 text-sm" data-testid="mw-imap-user" />
                <input type="password" value={cfg.password || ""} onChange={(e) => setCfg({ ...cfg, password: e.target.value })} placeholder="Passwort" className="rounded-sm border border-gray-300 px-3 py-2 text-sm" data-testid="mw-imap-password" />
                <div className="flex items-center gap-3">
                    <input value={cfg.folder || "INBOX"} onChange={(e) => setCfg({ ...cfg, folder: e.target.value })} placeholder="Ordner" className="w-28 rounded-sm border border-gray-300 px-3 py-2 text-sm" data-testid="mw-imap-folder" />
                    <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                        <input type="checkbox" checked={!!cfg.active} onChange={(e) => setCfg({ ...cfg, active: e.target.checked })} className="w-4 h-4" data-testid="mw-imap-active" /> Aktiv
                    </label>
                </div>
            </div>
            <div className="flex gap-2 mt-3">
                <button onClick={save} disabled={busy} className="inline-flex items-center gap-1 bg-[#111827] text-white rounded-sm px-3 py-2 text-xs font-bold hover:bg-black" data-testid="mw-imap-save"><Save className="w-3.5 h-3.5" /> Speichern</button>
                <button onClick={check} className="inline-flex items-center gap-1 border border-gray-300 rounded-sm px-3 py-2 text-xs font-bold hover:bg-gray-50" data-testid="mw-imap-check"><RefreshCw className="w-3.5 h-3.5" /> Jetzt prüfen</button>
            </div>
        </div>
    );
}

function DataInboxPanel() {
    // Jul-2026 (User-Wunsch) — Wissens-Übersicht: automatisch geladene Dateien
    // & Themen des Daten-Postfachs (daten@) mit Quelle und Datum.
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const load = () => api.get("/admin/knowledge-mail/entries")
        .then((r) => setItems(r.data?.items || []))
        .catch(() => {})
        .finally(() => setLoading(false));
    useEffect(() => { load(); }, []);
    const del = async (id) => {
        try { await api.delete(`/admin/knowledge-mail/entries/${id}`); toast.message("Eintrag entfernt"); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen"); }
    };
    const kindLabel = (k) => (k === "attachment" ? "Anhang" : k === "link" ? "Link-Download" : "E-Mail-Text");
    return (
        <div className="mt-6" data-testid="mw-data-inbox-panel">
            <h2 className="text-lg font-black mb-2 flex items-center gap-2"><Mail className="w-5 h-5 text-[#FFCC00]" /> Daten-Postfach — Wissensdatenbank ({items.length})</h2>
            <div className="bg-white border border-gray-200 rounded-sm divide-y divide-gray-100 max-h-[420px] overflow-y-auto">
                {loading ? (
                    <div className="px-3 py-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline text-gray-400" /></div>
                ) : items.length === 0 ? (
                    <div className="px-3 py-6 text-sm text-gray-400 text-center">Noch keine automatisch geladenen Dateien/Themen — Mails an das Daten-Postfach werden hier gelistet.</div>
                ) : items.map((e) => (
                    <div key={e.id} className="px-3 py-2 flex items-start gap-2" data-testid={`mw-data-entry-${e.id}`}>
                        <span className={`mt-0.5 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm shrink-0 ${e.source_kind === "link" ? "bg-sky-100 text-sky-700" : e.source_kind === "attachment" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>
                            {kindLabel(e.source_kind)}
                        </span>
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-bold truncate">{e.filename}</div>
                            <div className="text-[11px] text-gray-500 truncate">Quelle: {e.email_from || "—"}{e.email_subject ? ` · „${e.email_subject}"` : ""}</div>
                            <div className="text-[10px] text-gray-400">{(e.created_at || "").slice(0, 16).replace("T", " ")} Uhr{e.chars ? ` · ${e.chars.toLocaleString("de-DE")} Zeichen für die KI` : ""}</div>
                        </div>
                        <button onClick={() => del(e.id)} className="text-red-500 hover:text-red-700 shrink-0" data-testid={`mw-data-entry-del-${e.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function AdminMarketWatchPage() {
    const { user } = useAuth();
    const [sources, setSources] = useState([]);
    const [topics, setTopics] = useState([]);
    const [overview, setOverview] = useState(null);
    const [crawling, setCrawling] = useState(false);

    const load = useCallback(() => {
        api.get("/admin/market-watch/sources").then((r) => setSources(r.data.items || [])).catch(() => {});
        api.get("/admin/market-watch/topics").then((r) => setTopics(r.data.items || [])).catch(() => {});
        api.get("/admin/market-watch/overview").then((r) => setOverview(r.data)).catch(() => {});
    }, []);
    useEffect(() => { load(); }, [load]);

    const seed = async () => {
        const r = await api.post("/admin/market-watch/sources/seed");
        toast.success(`${r.data.added} Standard-Quellen eingetragen`); load();
    };
    const crawl = async () => {
        setCrawling(true);
        try { await api.post("/admin/market-watch/crawl"); toast.success("Crawl gestartet — Themen erscheinen in wenigen Minuten (Seite neu laden)."); }
        catch { toast.error("Crawl fehlgeschlagen"); }
        finally { setCrawling(false); }
    };
    const delSource = async (id) => { await api.delete(`/admin/market-watch/sources/${id}`); load(); };
    const toggleSource = async (s) => { await api.patch(`/admin/market-watch/sources/${s.id}`, { active: !s.active }); load(); };
    const delTopic = async (id) => { await api.delete(`/admin/market-watch/topics/${id}`); setTopics((p) => p.filter((t) => t.id !== id)); };

    if (!user || user.role !== "admin") return <div className="p-8 text-sm">Nur für Admins.</div>;

    return (
        <div className="max-w-6xl mx-auto px-4 py-8" data-testid="admin-market-watch-page">
            <h1 className="text-3xl font-black tracking-tight text-[#111827] flex items-center gap-2">
                <Radar className="w-7 h-7 text-[#FFCC00]" /> Marktbeobachtung
            </h1>
            <p className="text-sm text-gray-500 mt-1 mb-6">Die KI sammelt Themen & Marktdaten aus beobachteten Webseiten und dem Newsletter-Postfach — für den Themen-Ticker im Social-Media-Studio und bessere Preisberechnungen.</p>

            {overview && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="mw-overview">
                    {[["Quellen", overview.sources], ["Themen gesamt", overview.topics], ["Neue Themen", overview.topics_new], ["KI-Entwürfe", overview.drafts]].map(([l, v]) => (
                        <div key={l} className="bg-white border border-gray-200 rounded-sm p-3">
                            <div className="text-2xl font-black font-mono-num">{v}</div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{l}</div>
                        </div>
                    ))}
                </div>
            )}

            <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-black flex items-center gap-2"><Globe className="w-5 h-5" /> Beobachtete Quellen ({sources.length})</h2>
                <div className="flex gap-2">
                    <button onClick={seed} className="text-xs font-bold border border-gray-300 rounded-sm px-3 py-1.5 hover:bg-gray-50" data-testid="mw-seed-btn">Standard-Quellen laden</button>
                    <button onClick={crawl} disabled={crawling} className="inline-flex items-center gap-1 text-xs font-bold bg-[#FFCC00] text-[#111827] rounded-sm px-3 py-1.5 hover:bg-yellow-400" data-testid="mw-crawl-btn">
                        {crawling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Jetzt crawlen
                    </button>
                </div>
            </div>
            <SourceForm onAdded={load} />
            <div className="mt-2 bg-white border border-gray-200 rounded-sm divide-y divide-gray-100 mb-8" data-testid="mw-sources-list">
                {sources.map((s) => (
                    <div key={s.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm ${CAT_COLOR[s.category] || CAT_COLOR.sonstiges}`}>{CAT_LABEL[s.category] || s.category}</span>
                        <div className="min-w-0 flex-1">
                            <div className="font-bold truncate">{s.label}</div>
                            <div className="text-[11px] text-gray-400 truncate">{s.url}{s.last_status ? ` · Letzter Lauf: ${s.last_status}${s.last_topics_added ? ` (+${s.last_topics_added} Themen)` : ""}` : ""}</div>
                        </div>
                        <button onClick={() => toggleSource(s)} className={`text-[10px] font-bold uppercase px-2 py-1 rounded-sm ${s.active !== false ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`} data-testid={`mw-source-toggle-${s.id}`}>
                            {s.active !== false ? "Aktiv" : "Pausiert"}
                        </button>
                        <button onClick={() => delSource(s.id)} className="text-red-500 hover:text-red-700" data-testid={`mw-source-del-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                ))}
                {sources.length === 0 && <div className="px-3 py-6 text-sm text-gray-400 text-center">Noch keine Quellen — „Standard-Quellen laden" klicken.</div>}
            </div>

            <KeywordsPanel />

            <DataInboxPanel />

            <VehicleStatsPanel />

            <SurveysAdminPanel />

            <div className="grid lg:grid-cols-2 gap-6">
                <div>
                    <h2 className="text-lg font-black mb-2">Gesammelte Themen ({topics.length})</h2>
                    <div className="bg-white border border-gray-200 rounded-sm divide-y divide-gray-100 max-h-[480px] overflow-y-auto" data-testid="mw-topics-list">
                        {topics.map((t) => (
                            <div key={t.id} className="px-3 py-2">
                                <div className="flex items-start gap-2">
                                    <span className="mt-0.5 text-[10px] font-mono-num font-bold px-1.5 py-0.5 rounded-sm bg-[#111827] text-[#FFCC00]" title="Reichweiten-Score">{t.reach_score}</span>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-bold flex items-center gap-2 flex-wrap">
                                            <span>{t.title}</span>
                                            {t.used_in_social_at && (
                                                <span className="text-[9px] font-bold uppercase bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-sm shrink-0" title={`Zuletzt in Social-Beitrag verwendet: ${String(t.used_in_social_at).slice(0, 10)}`} data-testid={`mw-topic-used-badge-${t.id}`}>
                                                    In Beitrag verwendet{Number(t.used_in_social_count) > 1 ? ` ${t.used_in_social_count}×` : ""}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-gray-500">{t.summary}</div>
                                        <div className="text-[10px] text-gray-400 mt-0.5">{(t.brands || []).join(", ") || "allgemein"} · {t.source_label}</div>
                                    </div>
                                    <button onClick={() => delTopic(t.id)} className="text-red-400 hover:text-red-600" data-testid={`mw-topic-del-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                            </div>
                        ))}
                        {topics.length === 0 && <div className="px-3 py-6 text-sm text-gray-400 text-center">Noch keine Themen — Crawl starten.</div>}
                    </div>
                </div>
                <ImapPanel />
            </div>
        </div>
    );
}
